import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Icon,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { CircleMarker, MapContainer, Polyline, Popup, TileLayer, Tooltip as LeafletTooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { FaArrowLeft, FaBus, FaCamera, FaEdit, FaEye, FaImage, FaMapMarkerAlt, FaPlus, FaSyncAlt, FaTrash, FaTruck, FaShuttleVan } from "react-icons/fa";
import { toStorageImageApiSegment } from "../../utils/storageImagePath";

const getTodayInputValue = () => {
  const today = new Date();
  today.setMinutes(today.getMinutes() - today.getTimezoneOffset());
  return today.toISOString().slice(0, 10);
};

const getTokenHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`
});

const fetchJson = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getTokenHeaders(),
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {})
    }
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || data.message || "Erro ao comunicar com o servidor");
  return data;
};

const normalizeText = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase();

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
};

const formatTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
};

const formatCategory = (value) => String(value || "-").replace(/_/g, " ");

const getPassageCamera = (passage) => passage.Camera || passage.camera || null;

const getImageFileName = (imagePath) => toStorageImageApiSegment(imagePath, "passages");

const FitBounds = ({ points }) => {
  const map = useMap();
  const boundsKey = points.map((point) => `${point.latitude},${point.longitude}`).join("|");

  useEffect(() => {
    if (!points.length) return;
    map.fitBounds(points.map((point) => [point.latitude, point.longitude]), { padding: [28, 28], maxZoom: 17 });
  }, [boundsKey, map, points]);

  return null;
};

const InspectionMiniMap = ({ mapPoints, passages }) => {
  const points = useMemo(() => {
    return (mapPoints || [])
      .map((point) => ({ ...point, latitude: Number(point.latitude), longitude: Number(point.longitude) }))
      .filter((point) => Number.isFinite(point.latitude) && Number.isFinite(point.longitude));
  }, [mapPoints]);

  const path = useMemo(() => {
    return (passages || [])
      .map((passage) => {
        const camera = getPassageCamera(passage);
        const latitude = Number(camera?.latitude);
        const longitude = Number(camera?.longitude);
        return Number.isFinite(latitude) && Number.isFinite(longitude) ? [latitude, longitude] : null;
      })
      .filter(Boolean);
  }, [passages]);

  if (!points.length) {
    return (
      <Flex h="360px" align="center" justify="center" direction="column" bg="gray.50" borderWidth="1px" borderColor="gray.200" borderRadius="md">
        <Icon as={FaMapMarkerAlt} color="gray.400" boxSize={7} />
        <Text mt={3} color="gray.600" fontSize="sm">Sem pontos georreferenciados para este dia</Text>
      </Flex>
    );
  }

  return (
    <Box h="360px" borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
      <MapContainer center={[points[0].latitude, points[0].longitude]} zoom={14} style={{ width: "100%", height: "100%" }} preferCanvas>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={points} />
        {path.length > 1 && (
          <Polyline
            positions={path}
            pathOptions={{ color: "#0f766e", weight: 4, opacity: 0.75, lineCap: "round", lineJoin: "round" }}
          />
        )}
        {points.map((point) => (
          <CircleMarker
            key={point.cameraId}
            center={[point.latitude, point.longitude]}
            radius={8 + Math.min(point.passageCount || 0, 8)}
            pathOptions={{ color: "#0f766e", fillColor: "#14b8a6", fillOpacity: 0.88, weight: 2 }}
          >
            <LeafletTooltip direction="top" offset={[0, -6]} opacity={1}>
              <Text fontWeight="bold">{point.name}</Text>
              <Text>{point.passageCount} passagem(ns)</Text>
            </LeafletTooltip>
            <Popup>
              <Box minW="180px">
                <Text fontWeight="bold">{point.name}</Text>
                <Text fontSize="sm" color="gray.600">{point.location || "Sem localização"}</Text>
                <Text fontSize="sm" mt={2}>Passagens: <strong>{point.passageCount}</strong></Text>
                <Text fontSize="xs" color="gray.500">Primeira: {formatTime(point.firstDetectedAt)}</Text>
                <Text fontSize="xs" color="gray.500">Última: {formatTime(point.lastDetectedAt)}</Text>
              </Box>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </Box>
  );
};

const EmptyState = ({ icon, title, detail }) => (
  <Flex py={10} align="center" justify="center" direction="column" color="gray.600" textAlign="center">
    <Icon as={icon} boxSize={7} color="gray.400" />
    <Text mt={3} fontWeight="semibold">{title}</Text>
    {detail && <Text fontSize="sm" mt={1}>{detail}</Text>}
  </Flex>
);

const InspectionOperationsScreen = ({ selectedTypeConfig, onBackToTypes }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [date, setDate] = useState(getTodayInputValue());
  const [vehicles, setVehicles] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [cameraSearch, setCameraSearch] = useState("");
  const [registrySearch, setRegistrySearch] = useState("");
  const [loadingLists, setLoadingLists] = useState(false);
  const [activeDetail, setActiveDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleForm, setVehicleForm] = useState({ plateNumber: "", vehicleCategory: "", description: "" });
  const [vehicleToRemove, setVehicleToRemove] = useState(null);
  const [imageModal, setImageModal] = useState({ open: false, passage: null, imageUrl: "", loading: false });
  const previousImageUrl = useRef("");
  const cancelRef = useRef();
  const toast = useToast();

  const vehicleModal = useDisclosure();
  const removeDialog = useDisclosure();

  const fetchVehicles = useCallback(async () => {
    if (!selectedTypeConfig) return;
    const data = await fetchJson(`/api/inspections/vehicles?inspectionType=${selectedTypeConfig.id}`);
    setVehicles(data.vehicles || []);
  }, [selectedTypeConfig]);

  const fetchCameras = useCallback(async () => {
    const data = await fetchJson("/api/cameras");
    setCameras((data || []).sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))));
  }, []);

  const fetchInitialLists = useCallback(async () => {
    if (!selectedTypeConfig) return;
    setLoadingLists(true);
    try {
      await Promise.all([fetchVehicles(), fetchCameras()]);
    } catch (error) {
      toast({ title: "Erro ao carregar fiscalização", description: error.message, status: "error", duration: 3500, isClosable: true });
    } finally {
      setLoadingLists(false);
    }
  }, [fetchCameras, fetchVehicles, selectedTypeConfig, toast]);

  useEffect(() => {
    fetchInitialLists();
    setActiveDetail(null);
    setDetailData(null);
  }, [fetchInitialLists]);

  const fetchDetail = useCallback(async () => {
    if (!selectedTypeConfig || !activeDetail) return;
    setLoadingDetail(true);
    try {
      const params = new URLSearchParams({ inspectionType: selectedTypeConfig.id, date, limit: "2000" });
      const url = activeDetail.kind === "vehicle"
        ? `/api/inspections/vehicle/${encodeURIComponent(activeDetail.entity.plateNumber)}/passages?${params.toString()}`
        : `/api/inspections/camera/${activeDetail.entity.id}/passages?${params.toString()}`;

      const data = await fetchJson(url);
      setDetailData(data);
    } catch (error) {
      toast({ title: "Erro ao buscar passagens", description: error.message, status: "error", duration: 3500, isClosable: true });
      setDetailData(null);
    } finally {
      setLoadingDetail(false);
    }
  }, [activeDetail, date, selectedTypeConfig, toast]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const filteredVehicles = useMemo(() => {
    const query = normalizeText(vehicleSearch);
    if (!query) return vehicles;
    return vehicles.filter((vehicle) => normalizeText(`${vehicle.plateNumber} ${vehicle.vehicleCategory} ${vehicle.description}`).includes(query));
  }, [vehicleSearch, vehicles]);

  const filteredCameras = useMemo(() => {
    const query = normalizeText(cameraSearch);
    if (!query) return cameras;
    return cameras.filter((camera) => normalizeText(`${camera.name} ${camera.location}`).includes(query));
  }, [cameraSearch, cameras]);

  const filteredRegistryVehicles = useMemo(() => {
    const query = normalizeText(registrySearch);
    if (!query) return vehicles;
    return vehicles.filter((vehicle) => normalizeText(`${vehicle.plateNumber} ${vehicle.vehicleCategory} ${vehicle.description}`).includes(query));
  }, [registrySearch, vehicles]);

  const openVehicleModal = (vehicle = null) => {
    setEditingVehicle(vehicle);
    setVehicleForm({
      plateNumber: vehicle?.plateNumber || "",
      vehicleCategory: vehicle?.vehicleCategory || selectedTypeConfig?.categories?.[0]?.value || "",
      description: vehicle?.description || ""
    });
    vehicleModal.onOpen();
  };

  const saveVehicle = async () => {
    if (!selectedTypeConfig) return;
    try {
      const body = JSON.stringify({
        ...vehicleForm,
        inspectionType: selectedTypeConfig.id
      });
      const url = editingVehicle ? `/api/inspections/vehicles/${editingVehicle.id}` : "/api/inspections/vehicles";
      const method = editingVehicle ? "PUT" : "POST";
      await fetchJson(url, { method, body });
      vehicleModal.onClose();
      setEditingVehicle(null);
      await fetchVehicles();
      toast({ title: editingVehicle ? "Veículo atualizado" : "Veículo cadastrado", status: "success", duration: 2200, isClosable: true });
    } catch (error) {
      toast({ title: "Erro ao salvar veículo", description: error.message, status: "error", duration: 3500, isClosable: true });
    }
  };

  const confirmRemoveVehicle = (vehicle) => {
    setVehicleToRemove(vehicle);
    removeDialog.onOpen();
  };

  const removeVehicle = async () => {
    if (!vehicleToRemove) return;
    try {
      await fetchJson(`/api/inspections/vehicles/${vehicleToRemove.id}`, { method: "DELETE" });
      removeDialog.onClose();
      setVehicleToRemove(null);
      await fetchVehicles();
      toast({ title: "Veículo removido", status: "success", duration: 2200, isClosable: true });
    } catch (error) {
      toast({ title: "Erro ao remover veículo", description: error.message, status: "error", duration: 3500, isClosable: true });
    }
  };

  const openPassageImage = async (passage) => {
    if (previousImageUrl.current) URL.revokeObjectURL(previousImageUrl.current);
    previousImageUrl.current = "";
    setImageModal({ open: true, passage, imageUrl: "", loading: true });

    try {
      const fileName = getImageFileName(passage.imagePath);
      if (!fileName) throw new Error("Passagem sem imagem");

      const response = await fetch(`/api/passages/image/${fileName}`, {
        headers: getTokenHeaders()
      });
      if (!response.ok) throw new Error("Imagem da passagem não encontrada");

      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);
      previousImageUrl.current = imageUrl;
      setImageModal({ open: true, passage, imageUrl, loading: false });
    } catch (error) {
      setImageModal({ open: true, passage, imageUrl: "", loading: false });
      toast({ title: "Erro ao abrir imagem", description: error.message, status: "error", duration: 3200, isClosable: true });
    }
  };

  const closeImageModal = () => {
    if (previousImageUrl.current) URL.revokeObjectURL(previousImageUrl.current);
    previousImageUrl.current = "";
    setImageModal({ open: false, passage: null, imageUrl: "", loading: false });
  };

  useEffect(() => {
    return () => {
      if (previousImageUrl.current) URL.revokeObjectURL(previousImageUrl.current);
    };
  }, []);

  const renderMonitorSelection = () => (
    <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5} alignItems="start">
      <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
        <Flex align="center" justify="space-between" p={4} borderBottomWidth="1px" borderColor="gray.200" gap={3} wrap="wrap">
          <HStack>
            <Icon as={selectedTypeConfig?.id === "PUBLIC_TRANSPORT" ? FaShuttleVan : FaTruck} color={`${selectedTypeConfig?.colorScheme}.500`} />
            <Heading size="sm">Veículos</Heading>
            <Badge colorScheme={selectedTypeConfig?.colorScheme}>{vehicles.length}</Badge>
          </HStack>
          <InputGroup maxW={{ base: "100%", md: "280px" }}>
            <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" /></InputLeftElement>
            <Input value={vehicleSearch} onChange={(event) => setVehicleSearch(event.target.value)} placeholder="Buscar placa ou tipo" />
          </InputGroup>
        </Flex>
        <Box maxH="560px" overflowY="auto">
          {loadingLists ? (
            <Flex py={10} justify="center"><Spinner /></Flex>
          ) : filteredVehicles.length === 0 ? (
            <EmptyState icon={FaBus} title="Nenhum veículo encontrado" detail="Use a aba de cadastro para adicionar placas" />
          ) : (
            <VStack spacing={0} align="stretch">
              {filteredVehicles.map((vehicle) => (
                <Box
                  as="button"
                  key={vehicle.id}
                  textAlign="left"
                  p={4}
                  borderBottomWidth="1px"
                  borderColor="gray.100"
                  _hover={{ bg: "gray.50" }}
                  onClick={() => setActiveDetail({ kind: "vehicle", entity: vehicle })}
                >
                  <Flex justify="space-between" align="center" gap={3}>
                    <Box>
                      <Text fontWeight="bold" fontSize="lg">{vehicle.plateNumber}</Text>
                      <Text fontSize="sm" color="gray.600">{vehicle.description || "Sem descrição"}</Text>
                    </Box>
                    <Badge colorScheme={selectedTypeConfig?.colorScheme}>{formatCategory(vehicle.vehicleCategory)}</Badge>
                  </Flex>
                </Box>
              ))}
            </VStack>
          )}
        </Box>
      </Box>

      <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
        <Flex align="center" justify="space-between" p={4} borderBottomWidth="1px" borderColor="gray.200" gap={3} wrap="wrap">
          <HStack>
            <Icon as={FaCamera} color="blue.500" />
            <Heading size="sm">Câmeras</Heading>
            <Badge colorScheme="blue">{cameras.length}</Badge>
          </HStack>
          <InputGroup maxW={{ base: "100%", md: "280px" }}>
            <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" /></InputLeftElement>
            <Input value={cameraSearch} onChange={(event) => setCameraSearch(event.target.value)} placeholder="Buscar câmera ou local" />
          </InputGroup>
        </Flex>
        <Box maxH="560px" overflowY="auto">
          {loadingLists ? (
            <Flex py={10} justify="center"><Spinner /></Flex>
          ) : filteredCameras.length === 0 ? (
            <EmptyState icon={FaCamera} title="Nenhuma câmera encontrada" />
          ) : (
            <VStack spacing={0} align="stretch">
              {filteredCameras.map((camera) => (
                <Box
                  as="button"
                  key={camera.id}
                  textAlign="left"
                  p={4}
                  borderBottomWidth="1px"
                  borderColor="gray.100"
                  _hover={{ bg: "gray.50" }}
                  onClick={() => setActiveDetail({ kind: "camera", entity: camera })}
                >
                  <Flex justify="space-between" align="center" gap={3}>
                    <Box>
                      <Text fontWeight="bold">{camera.name}</Text>
                      <Text fontSize="sm" color="gray.600">{camera.location || "Sem localização"}</Text>
                    </Box>
                    {camera.latitude && camera.longitude && <Badge colorScheme="green">GPS</Badge>}
                  </Flex>
                </Box>
              ))}
            </VStack>
          )}
        </Box>
      </Box>
    </SimpleGrid>
  );

  const renderPassagesTable = (passages, mode) => (
    <TableContainer bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md">
      <Table variant="simple" size="sm">
        <Thead bg="gray.50">
          <Tr>
            {mode === "camera" && <Th>Placa</Th>}
            {mode === "vehicle" && <Th>Câmera</Th>}
            <Th>Local</Th>
            <Th>Horário</Th>
            <Th>Tipo</Th>
            <Th isNumeric>Confiança</Th>
            <Th textAlign="center">Foto</Th>
          </Tr>
        </Thead>
        <Tbody>
          {passages.map((passage) => {
            const camera = getPassageCamera(passage);
            const vehicle = passage.inspectionVehicle;
            return (
              <Tr key={passage.id} cursor="pointer" _hover={{ bg: "gray.50" }} onClick={() => openPassageImage(passage)}>
                {mode === "camera" && (
                  <Td fontWeight="bold">
                    <HStack spacing={2}>
                      <Text>{passage.plateNumber}</Text>
                      {vehicle?.vehicleCategory && <Badge colorScheme={selectedTypeConfig?.colorScheme}>{formatCategory(vehicle.vehicleCategory)}</Badge>}
                    </HStack>
                  </Td>
                )}
                {mode === "vehicle" && <Td fontWeight="semibold">{camera?.name || `Câmera ${passage.cameraId}`}</Td>}
                <Td>{camera?.location || "-"}</Td>
                <Td>{formatDateTime(passage.detectedAt)}</Td>
                <Td>{passage.vehicleType || "-"}</Td>
                <Td isNumeric>{passage.confidence ? `${(Number(passage.confidence) * 100).toFixed(1)}%` : "-"}</Td>
                <Td textAlign="center">
                  <Tooltip label="Abrir imagem da passagem">
                    <IconButton
                      aria-label="Abrir imagem da passagem"
                      icon={<Icon as={FaImage} />}
                      size="sm"
                      variant="ghost"
                      colorScheme="teal"
                      onClick={(event) => {
                        event.stopPropagation();
                        openPassageImage(passage);
                      }}
                    />
                  </Tooltip>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );

  const renderDetail = () => {
    const passages = detailData?.passages || [];
    const isVehicleMode = activeDetail?.kind === "vehicle";
    const title = isVehicleMode
      ? activeDetail.entity.plateNumber
      : activeDetail.entity.name;
    const subtitle = isVehicleMode
      ? `${selectedTypeConfig.title} - ${formatCategory(activeDetail.entity.vehicleCategory)}`
      : activeDetail.entity.location || "Sem localização";
    const distinctCameras = new Set(passages.map((passage) => passage.cameraId)).size;
    const distinctVehicles = new Set(passages.map((passage) => passage.plateNumber)).size;

    return (
      <Box>
        <Flex justify="space-between" align={{ base: "start", xl: "center" }} gap={4} mb={5} direction={{ base: "column", xl: "row" }}>
          <HStack align="center" spacing={3}>
            <Button leftIcon={<Icon as={FaArrowLeft} />} variant="outline" onClick={() => { setActiveDetail(null); setDetailData(null); }}>
              Voltar
            </Button>
            <Box>
              <Heading size="md">{title}</Heading>
              <Text color="gray.600" fontSize="sm">{subtitle}</Text>
            </Box>
          </HStack>
          <HStack spacing={3} wrap="wrap">
            <FormControl maxW="180px">
              <FormLabel fontSize="sm" mb={1}>Data</FormLabel>
              <Input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
            </FormControl>
            <Button alignSelf="end" leftIcon={<Icon as={FaSyncAlt} />} onClick={fetchDetail} isLoading={loadingDetail} colorScheme="teal" variant="outline">
              Atualizar
            </Button>
          </HStack>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={5}>
          <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
            <Text fontSize="sm" color="gray.600">Passagens no dia</Text>
            <Text fontSize="2xl" fontWeight="bold">{passages.length}</Text>
          </Box>
          <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
            <Text fontSize="sm" color="gray.600">{isVehicleMode ? "Câmeras visitadas" : "Veículos registrados"}</Text>
            <Text fontSize="2xl" fontWeight="bold">{isVehicleMode ? distinctCameras : distinctVehicles}</Text>
          </Box>
          <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
            <Text fontSize="sm" color="gray.600">Última passagem</Text>
            <Text fontSize="md" fontWeight="bold">{passages.length ? formatDateTime(passages[isVehicleMode ? passages.length - 1 : 0].detectedAt) : "-"}</Text>
          </Box>
        </SimpleGrid>

        {loadingDetail ? (
          <Flex py={16} justify="center"><Spinner size="xl" color="teal.500" thickness="4px" /></Flex>
        ) : passages.length === 0 ? (
          <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md">
            <EmptyState icon={FaEye} title="Nenhuma passagem encontrada" detail="Altere a data ou selecione outro item" />
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, xl: isVehicleMode ? 2 : 1 }} spacing={5} alignItems="start">
            <Box>{renderPassagesTable(passages, isVehicleMode ? "vehicle" : "camera")}</Box>
            {isVehicleMode && (
              <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
                <HStack mb={3} spacing={2}>
                  <Icon as={FaMapMarkerAlt} color="teal.500" />
                  <Heading size="sm">Mapa de passagens</Heading>
                </HStack>
                <InspectionMiniMap mapPoints={detailData?.mapPoints || []} passages={passages} />
              </Box>
            )}
          </SimpleGrid>
        )}
      </Box>
    );
  };

  const renderRegistry = () => (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
      <Flex align={{ base: "start", lg: "center" }} justify="space-between" p={4} borderBottomWidth="1px" borderColor="gray.200" gap={3} direction={{ base: "column", lg: "row" }}>
        <Box>
          <Heading size="sm">Veículos cadastrados</Heading>
          <Text color="gray.600" fontSize="sm">{selectedTypeConfig.label}</Text>
        </Box>
        <HStack spacing={3} w={{ base: "100%", lg: "auto" }}>
          <InputGroup maxW={{ base: "100%", lg: "320px" }}>
            <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" /></InputLeftElement>
            <Input value={registrySearch} onChange={(event) => setRegistrySearch(event.target.value)} placeholder="Buscar placa, tipo ou descrição" />
          </InputGroup>
          <Button leftIcon={<Icon as={FaPlus} />} colorScheme="teal" onClick={() => openVehicleModal()} minW="150px">
            Cadastrar
          </Button>
        </HStack>
      </Flex>

      {loadingLists ? (
        <Flex py={10} justify="center"><Spinner /></Flex>
      ) : filteredRegistryVehicles.length === 0 ? (
        <EmptyState icon={selectedTypeConfig.id === "PUBLIC_TRANSPORT" ? FaBus : FaTruck} title="Nenhum veículo cadastrado" />
      ) : (
        <TableContainer>
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Placa</Th>
                <Th>Categoria</Th>
                <Th>Descrição</Th>
                <Th>Cadastro</Th>
                <Th textAlign="right">Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredRegistryVehicles.map((vehicle) => (
                <Tr key={vehicle.id}>
                  <Td fontWeight="bold">{vehicle.plateNumber}</Td>
                  <Td><Badge colorScheme={selectedTypeConfig.colorScheme}>{formatCategory(vehicle.vehicleCategory)}</Badge></Td>
                  <Td>{vehicle.description || "-"}</Td>
                  <Td>{formatDateTime(vehicle.createdAt)}</Td>
                  <Td>
                    <HStack justify="flex-end" spacing={2}>
                      <Tooltip label="Editar veículo">
                        <IconButton aria-label="Editar veículo" icon={<Icon as={FaEdit} />} size="sm" onClick={() => openVehicleModal(vehicle)} />
                      </Tooltip>
                      <Tooltip label="Remover veículo">
                        <IconButton aria-label="Remover veículo" icon={<Icon as={FaTrash} />} size="sm" colorScheme="red" variant="outline" onClick={() => confirmRemoveVehicle(vehicle)} />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  return (
    <Box>
      <Flex justify="space-between" align={{ base: "start", lg: "center" }} gap={4} mb={5} direction={{ base: "column", lg: "row" }}>
        <HStack spacing={3} align="center">
          <Button leftIcon={<Icon as={FaArrowLeft} />} variant="outline" onClick={onBackToTypes}>
            Fiscalizações
          </Button>
          <Flex boxSize="44px" borderRadius="md" bg={`${selectedTypeConfig.colorScheme}.50`} color={`${selectedTypeConfig.colorScheme}.600`} align="center" justify="center">
            <Icon as={selectedTypeConfig.icon} boxSize={5} />
          </Flex>
          <Box>
            <Heading size="lg">{selectedTypeConfig.title}</Heading>
            <Text color="gray.600" fontSize="sm">{selectedTypeConfig.label}</Text>
          </Box>
        </HStack>
        <Button leftIcon={<Icon as={FaSyncAlt} />} onClick={fetchInitialLists} isLoading={loadingLists} variant="outline" colorScheme="teal">
          Atualizar listas
        </Button>
      </Flex>

      <Tabs index={tabIndex} onChange={(index) => { setTabIndex(index); setActiveDetail(null); setDetailData(null); }} colorScheme="teal" variant="enclosed">
        <TabList bg="gray.100" borderBottomWidth="1px" borderColor="gray.300">
          <Tab
            bg="gray.200"
            color="gray.500"
            fontWeight="medium"
            borderColor="gray.300"
            _hover={{ bg: "gray.300", color: "gray.700" }}
            _selected={{ bg: "white", color: "teal.700", fontWeight: "semibold", borderColor: "gray.300", borderBottomColor: "white" }}
          >Monitoramento</Tab>
          <Tab
            bg="gray.200"
            color="gray.500"
            fontWeight="medium"
            borderColor="gray.300"
            _hover={{ bg: "gray.300", color: "gray.700" }}
            _selected={{ bg: "white", color: "teal.700", fontWeight: "semibold", borderColor: "gray.300", borderBottomColor: "white" }}
          >Veículos cadastrados</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>{activeDetail ? renderDetail() : renderMonitorSelection()}</TabPanel>
          <TabPanel px={0}>{renderRegistry()}</TabPanel>
        </TabPanels>
      </Tabs>

      <Modal isOpen={vehicleModal.isOpen} onClose={vehicleModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingVehicle ? "Editar veículo" : "Cadastrar veículo"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3} isRequired>
              <FormLabel>Placa</FormLabel>
              <Input
                value={vehicleForm.plateNumber}
                onChange={(event) => setVehicleForm((prev) => ({ ...prev, plateNumber: event.target.value.toUpperCase() }))}
                placeholder="AAA0A00"
              />
            </FormControl>
            <FormControl mb={3} isRequired>
              <FormLabel>Categoria</FormLabel>
              <Select value={vehicleForm.vehicleCategory} onChange={(event) => setVehicleForm((prev) => ({ ...prev, vehicleCategory: event.target.value }))}>
                {(selectedTypeConfig?.categories || []).map((category) => (
                  <option key={category.value} value={category.value}>{category.label}</option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Descrição</FormLabel>
              <Textarea
                value={vehicleForm.description}
                onChange={(event) => setVehicleForm((prev) => ({ ...prev, description: event.target.value }))}
                placeholder="Linha, prefixo, empresa ou observação"
                rows={3}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={vehicleModal.onClose}>Cancelar</Button>
            <Button colorScheme="teal" onClick={saveVehicle}>{editingVehicle ? "Salvar" : "Cadastrar"}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={removeDialog.isOpen} leastDestructiveRef={cancelRef} onClose={removeDialog.onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Remover veículo</AlertDialogHeader>
            <AlertDialogBody>
              Remover a placa {vehicleToRemove?.plateNumber} da fiscalização {selectedTypeConfig?.title}?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={removeDialog.onClose}>Cancelar</Button>
              <Button colorScheme="red" onClick={removeVehicle} ml={3}>Remover</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Modal isOpen={imageModal.open} onClose={closeImageModal} size="4xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={FaImage} color="teal.500" />
              <Box>
                <Text>Passagem {imageModal.passage?.plateNumber}</Text>
                <Text fontSize="sm" color="gray.600" fontWeight="normal">{formatDateTime(imageModal.passage?.detectedAt)}</Text>
              </Box>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {imageModal.loading ? (
              <Flex h="420px" align="center" justify="center"><Spinner size="xl" color="teal.500" thickness="4px" /></Flex>
            ) : imageModal.imageUrl ? (
              <Image src={imageModal.imageUrl} alt="Imagem da passagem" maxH="70vh" mx="auto" borderRadius="md" />
            ) : (
              <EmptyState icon={FaImage} title="Imagem indisponível" />
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={closeImageModal}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default InspectionOperationsScreen;
